// Opening hours model shared by the Settings → Opening Hours editor and the
// booking module's per-service hours ("Use opening hours" inherit + the
// outside-opening-hours flag). Stored in site_config.opening_hours_json.
//
// Storage shape: { mon: [{ open, close }, ...], ... } — multiple windows per
// day (lunch/dinner splits). The legacy single-window shape
// ({ mon: { open, close } }) is still parsed; the template's footer and
// JSON-LD accept both.

export const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export const DAY_LABELS: Record<string, string> = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday"
};

/** day key ↔ the API's day_of_week numbers (0 = Sunday .. 6 = Saturday). */
export const DAY_TO_NUMBER: Record<string, number> = {
  sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6
};
const NUMBER_TO_DAY = Object.fromEntries(
  Object.entries(DAY_TO_NUMBER).map(([day, number]) => [number, day])
) as Record<number, string>;

/** Weekday name per API day number (0 = Sunday .. 6 = Saturday). */
export const WEEKDAY_LABELS: Record<number, string> = Object.fromEntries(
  Object.entries(DAY_TO_NUMBER).map(([day, number]) => [number, DAY_LABELS[day]])
);

/** Grid display order Monday-first; values stay API day_of_week numbers. */
export const WEEK_DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

export interface HoursWindow {
  open: string;
  close: string;
}

/** Full week map — every day present, closed days as empty arrays. */
export type WeeklyHours = Record<string, HoursWindow[]>;

/** A weekly window in the booking API's rule shape. */
export interface HoursRule {
  id?: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
}

export function emptyWeek(): WeeklyHours {
  return Object.fromEntries(DAYS.map((day) => [day, []]));
}

/**
 * Parse the stored opening_hours_json string into a full week map. Accepts
 * the multi-window and legacy single-window shapes; missing or invalid input
 * yields a closed week.
 */
export function parseOpeningHours(json: string | null | undefined): WeeklyHours {
  let stored: Record<string, unknown> = {};
  if (typeof json === "string" && json.trim()) {
    try {
      const parsed = JSON.parse(json);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        stored = parsed;
      }
    } catch {
      // fall through to a closed week
    }
  }

  const week = emptyWeek();
  for (const day of DAYS) {
    const entry = stored[day];
    const list = Array.isArray(entry) ? entry : [entry];
    for (const candidate of list) {
      const { open, close } = (candidate ?? {}) as { open?: unknown; close?: unknown };
      if (typeof open === "string" && open && typeof close === "string" && close) {
        week[day].push({ open, close });
      }
    }
    week[day].sort((a, b) => a.open.localeCompare(b.open));
  }
  return week;
}

/**
 * Serialize the editor's week map back to the stored JSON string. Days
 * without complete windows are omitted (closed); null when every day is.
 */
export function serializeOpeningHours(week: WeeklyHours): string | null {
  const out: Record<string, HoursWindow[]> = {};
  for (const day of DAYS) {
    const windows = (week?.[day] ?? []).filter((window) => window.open && window.close);
    if (windows.length > 0) out[day] = windows;
  }
  return Object.keys(out).length > 0 ? JSON.stringify(out) : null;
}

/** Week map → the booking API's rule shape (for the shared grid / inherit). */
export function weekToRules(week: WeeklyHours): HoursRule[] {
  const rules: HoursRule[] = [];
  for (const day of DAYS) {
    for (const window of week[day] ?? []) {
      rules.push({ day_of_week: DAY_TO_NUMBER[day], start_time: window.open, end_time: window.close });
    }
  }
  return rules;
}

/** The booking API's rule shape → week map (grid state → serialization). */
export function rulesToWeek(rules: HoursRule[]): WeeklyHours {
  const week = emptyWeek();
  for (const rule of rules) {
    const day = NUMBER_TO_DAY[rule.day_of_week];
    if (!day || !rule.start_time || !rule.end_time) continue;
    week[day].push({ open: rule.start_time, close: rule.end_time });
  }
  for (const day of DAYS) week[day].sort((a, b) => a.open.localeCompare(b.open));
  return week;
}

/**
 * True when a rule's window sits fully inside one of the opening-hours
 * windows for its day. Used for the "outside opening hours" flag — a flag,
 * not a validation error: booking outside opening hours is allowed.
 */
export function isWithinOpeningHours(rule: HoursRule, openingHours: WeeklyHours): boolean {
  const day = NUMBER_TO_DAY[rule.day_of_week];
  const windows = day ? openingHours[day] ?? [] : [];
  return windows.some((window) => rule.start_time >= window.open && rule.end_time <= window.close);
}
